import type { RouteObject } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import { PolicySummaryView } from "@/pages/(home)/(company)/settings/company-policies-settings/policies/_components/policy-summary-view";
import { importWithRetry } from "@/utils/lazyWithRetry";

const companyPolicySettingsCreationPage = async () => {
  const { CompanyPoliciesForm } = await importWithRetry(
    () => import("../../pages/(home)/(company)/settings/company-policies-settings/policies/_components/policy-creation-form"),
  );
  return { Component: CompanyPoliciesForm };
};

const companyPolicySettingsCategoryPage = async () => {
  const { CompanyPoliciesSettings } = await importWithRetry(
    () => import("../../pages/(home)/(company)/settings/company-policies-settings"),
  );
  return { Component: CompanyPoliciesSettings };
};

const companyPolicySettingsCategoryCreationPage = async () => {
  const { CompanyPoliciesCategoryForm } = await importWithRetry(
    () => import("../../pages/(home)/(company)/settings/company-policies-settings/categories/_components/category-form"),
  );
  return { Component: CompanyPoliciesCategoryForm };
};

export const DYNAMIC_COMPANY_POLICIES_ID_KEY = "companyPoliciesId";
export const DYNAMIC_COMPANY_POLICIES_CATEGORY_ID_KEY = "categoryId";

export enum COMPANY_POLICIES_PATH_SEGMENTS {
  ROOT = "settings/company-policies",
  CREATE = "create",
  DETAILS = `:${DYNAMIC_COMPANY_POLICIES_ID_KEY}`,
  EDIT = "edit",
}

export enum COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS {
  ROOT = "settings/company-policies/category",
  CREATE = "create-new-category",
  DETAILS = `:${DYNAMIC_COMPANY_POLICIES_CATEGORY_ID_KEY}`,
  EDIT = "edit",
}

export const companyPoliciesSettingsRoutes: RouteObject = {
  path: COMPANY_POLICIES_PATH_SEGMENTS.ROOT,
  HydrateFallback: Spinner,
  // NO index route here. `/company/settings/company-policies` is owned by the
  // Company Settings tab layout (`companySettingsRoutes` → the `company-policies`
  // hTab). An index route on this branch scores higher than that tab (same path,
  // +2 for being an index) so it won every match and rendered the settings panel
  // WITHOUT the Settings tab bar. This branch exists only for the full-page
  // policy screens below.
  children: [
    {
      path: COMPANY_POLICIES_PATH_SEGMENTS.CREATE,
      lazy: companyPolicySettingsCreationPage,
    },
    {
      path: COMPANY_POLICIES_PATH_SEGMENTS.DETAILS,
      children: [
        {
          index: true,
          Component: PolicySummaryView,
        },
        {
          path: COMPANY_POLICIES_PATH_SEGMENTS.EDIT,
          lazy: companyPolicySettingsCreationPage,
        },
      ],
    },
  ],
};

export const companyPoliciesSettingsCategoryRoutes: RouteObject = {
  path: COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.ROOT,
  HydrateFallback: Spinner,
  children: [
    {
      index: true,
      lazy: companyPolicySettingsCategoryPage,
    },
    {
      path: COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.CREATE,
      lazy: companyPolicySettingsCategoryCreationPage,
    },
    {
      path: COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.DETAILS,
      children: [
        {
          path: COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.EDIT,
          lazy: companyPolicySettingsCategoryCreationPage,
        },
      ],
    },
  ],
};

export const COMPANY_POLICIES_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_POLICIES_PATH_SEGMENTS.ROOT)),
  CREATE: withCompanyPrefix(
    addSlashes(
      COMPANY_POLICIES_PATH_SEGMENTS.ROOT,
      COMPANY_POLICIES_PATH_SEGMENTS.CREATE,
    ),
  ),
  VIEW: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_POLICIES_PATH_SEGMENTS.ROOT, String(id)),
    ),
  EDIT: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_POLICIES_PATH_SEGMENTS.ROOT,
        String(id),
        COMPANY_POLICIES_PATH_SEGMENTS.EDIT,
      ),
    ),
});

export const COMPANY_POLICIES_CATEGORY_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(
    addSlashes(COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.ROOT),
  ),
  CREATE: withCompanyPrefix(
    addSlashes(
      COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.ROOT,
      COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.CREATE,
    ),
  ),
  EDIT: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.ROOT,
        String(id),
        COMPANY_POLICIES_CATEGORY_PATH_SEGMENTS.EDIT,
      ),
    ),
});
